/**
 * Soft≠Live Page188 Soft≠Live — Response Drill residual liquid
 * (Page39 / Wave23 buses + Page92 Soft≠Live · Page188 Soft≠Live CTAs retained).
 * Mount: /response/drill
 *
 * Buses (isolated — fail closed; no Soft invent cascade):
 *   GET /response/drill/status                 always-on posture SoT
 *   POST /simulations · …/run dry_run · report practice bus
 *   …/live-approval/{request,approve,GET} + live run  HITL bus
 *
 * Soft residual deleted:
 *  - bare Run practice / Request / Refresh / Approve / Run live
 *  - Soft “live drill approved” invent without Live POSTs
 *  - Soft dry_run success without create→run→report chain
 *  - Soft practice when SIMULATION_ENABLED off
 *
 * Rollback: drop residual + /response/drill/status; pins prev v287/169.
 */
import { useState } from "react";
import { html, Ico, KV, Card, Field, Input, Alert, Sec, go } from "./ui.js";
import { path, useApi, useAction, api } from "./api.js";
import { softResponseErrorOf } from "./response-live.js";
import { useSession } from "./session.js";
import { envelopeSummary } from "./action-envelope.js";

const SCENARIOS = [
  { id: "simulation.drill", label: "Practice drill" },
  { id: "warehouse.fire_drill", label: "Warehouse fire drill" },
];

/** Soft≠Live Page188: missing contract ops must not crash the desk. */
function safeDrillPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_drill_status") return "/response/drill/status";
    if (op === "post__simulations") return "/simulations";
    if (op === "post__simulations_p_run") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/simulations/${encodeURIComponent(id)}/run`;
    }
    if (op === "get__simulations_p_report") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/simulations/${encodeURIComponent(id)}/report`;
    }
    if (op === "post__simulations_p_live-approval_request") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/simulations/${encodeURIComponent(id)}/live-approval/request`;
    }
    if (op === "post__simulations_p_live-approval_approve") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/simulations/${encodeURIComponent(id)}/live-approval/approve`;
    }
    if (op === "get__simulations_p_live-approval") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/simulations/${encodeURIComponent(id)}/live-approval`;
    }
    if (op === "post__ai_needs-you_approval-token") return "/ai/needs-you/approval-token";
    if (op === "get__ai_needs-you") return "/ai/needs-you";
    return "";
  }
}

/** Merge live-approval + Needs You gate ids. Sibling fields must not be dropped. */
export function mergeDrillApproval(out, simId) {
  const pack = out && typeof out === "object" ? out : {};
  const approval = (pack.item && typeof pack.item === "object")
    ? pack.item
    : ((pack.approval && typeof pack.approval === "object") ? pack.approval : pack);
  const merged = { ...(approval && typeof approval === "object" ? approval : {}) };
  const nid = String(pack.needs_you_id || merged.needs_you_id || "").trim();
  const ih = String(
    pack.intent_hash || merged.intent_hash || (simId ? `drill:${simId}` : "")
  ).trim();
  if (nid) merged.needs_you_id = nid;
  if (ih) merged.intent_hash = ih;
  return merged;
}

/** Pure: seat chips from Live Response Drill posture (Page188). */
export function responseDrillSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const sMax = Number(lim.steps_paint_max);
  const jMax = Number(lim.approval_json_max);
  return {
    createAllowed: p.create_allowed === true,
    dryRunAllowed: p.dry_run_allowed === true,
    reportAllowed: p.report_allowed === true,
    liveRequestAllowed: p.live_request_allowed === true,
    liveApproveAllowed: p.live_approve_allowed === true,
    liveRunAllowed: p.live_run_allowed === true,
    approvalReadAllowed: p.approval_read_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    writeAllowed: p.write_allowed === true,
    engineArmed: p.engine_armed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    stepsPaintMax: Number.isFinite(sMax) && sMax > 0 ? Math.floor(sMax) : 40,
    approvalJsonMax: Number.isFinite(jMax) && jMax > 0 ? Math.floor(jMax) : 1000,
  };
}

/** Pure: report steps without inventing OK. */
export function drillStepsOf(reportPack, runPack) {
  const report = (reportPack && reportPack.report) || reportPack || {};
  const run = (runPack && runPack.run) || runPack || {};
  const steps = report.steps || run.steps || [];
  return Array.isArray(steps) ? steps : [];
}

/** Pure: clamp steps paint — no Soft invent. */
export function drillStepsClampOf(steps, maxN) {
  const list = Array.isArray(steps) ? steps : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 40;
  return list.slice(0, cap);
}

export function DrillDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isUser = role === "user";
  const isSaStaff = role === "sa_staff";
  const { run, busy, error, envelope } = useAction();

  const posture = useApi(safeDrillPath("get__response_drill_status"));
  const post = posture.data || {};
  const seat = responseDrillSeatOf(posture.loading ? null : post);

  const engineOff = !seat.engineArmed;
  const [pane, setPane] = useState("practice");
  const [name, setName] = useState("Quarterly alarm drill");
  const [eventType, setEventType] = useState("simulation.drill");
  const [simId, setSimId] = useState("");
  const [steps, setSteps] = useState(null);
  const [approvalNote, setApprovalNote] = useState("");
  const [approvalState, setApprovalState] = useState(null);

  const reRead = () => {
    posture.reload && posture.reload();
    toast("Drill access refreshed", "go");
  };

  const runPractice = async () => {
    if (!seat.createAllowed || !seat.dryRunAllowed) {
      toast(engineOff
        ? "Practice drills are turned off on this site"
        : "Practice sealed", "warn");
      return;
    }
    if (!name.trim() || !eventType) {
      toast("Name and scenario required", "warn");
      return;
    }
    if (/^alarm\./i.test(eventType) || /panic/i.test(eventType)) {
      toast("Live alarm/panic types are blocked — pick a practice scenario", "bad");
      return;
    }
    toast("Creating practice scenario…", "warn");
    const created = await run(safeDrillPath("post__simulations"), {
      body: {
        name: name.trim(),
        simulated_event_type: eventType,
        device_ids: ["practice-drill"],
        user_behaviors: ["acknowledge"],
      },
    });
    if (!created) {
      toast(error || "Could not create practice drill", "bad");
      return;
    }
    const id = String((created.item && created.item.simulation_id) || created.simulation_id || "").trim();
    if (!id) { toast("Could not start drill — no practice id", "bad"); return; }
    setSimId(id);
    toast("Starting practice run…", "warn");
    const runOut = await run(safeDrillPath("post__simulations_p_run", { pathParams: [id] }), {
      body: { mode: "dry_run" },
    });
    if (!runOut) {
      toast("Drill run refused", "bad");
      return;
    }
    let report = null;
    if (seat.reportAllowed) {
      try {
        report = await api(safeDrillPath("get__simulations_p_report", { pathParams: [id] }), { method: "GET" });
      } catch (e) {
        toast(String((e && e.message) || e), "warn");
      }
    }
    const list = drillStepsClampOf(drillStepsOf(report, runOut), seat.stepsPaintMax);
    setSteps(list);
    toast("Practice complete — production untouched", "go");
  };

  const requestLive = async () => {
    if (!seat.liveRequestAllowed) {
      toast("Live-request sealed", "warn");
      return;
    }
    if (!simId) { toast("Run a practice drill first", "warn"); return; }
    const out = await run(safeDrillPath("post__simulations_p_live-approval_request", { pathParams: [simId] }), {
      body: approvalNote.trim() ? { reason: approvalNote.trim(), note: approvalNote.trim() } : {},
    });
    if (!out) { toast("Live-approval request refused · Live", "bad"); return; }
    setApprovalState(mergeDrillApproval(out, simId));
    toast("Live approval requested — waiting for manager", "warn");
  };

  const refreshApproval = async () => {
    if (!seat.approvalReadAllowed) {
      toast("Approval re-read sealed", "warn");
      return;
    }
    if (!simId) { toast("Run a practice drill first", "warn"); return; }
    try {
      const out = await api(safeDrillPath("get__simulations_p_live-approval", { pathParams: [simId] }), { method: "GET" });
      setApprovalState(mergeDrillApproval(out, simId));
      toast("Approval refreshed", "go");
    } catch (e) {
      toast(String((e && e.message) || e) + " · Live", "warn");
    }
  };

  const approveLive = async () => {
    if (!seat.liveApproveAllowed) {
      toast("Approve is sealed on this seat", "warn");
      return;
    }
    if (!simId) { toast("Run a practice drill first", "warn"); return; }
    const out = await run(safeDrillPath("post__simulations_p_live-approval_approve", { pathParams: [simId] }), {
      body: {},
    });
    if (!out) { toast("Approve refused", "bad"); return; }
    setApprovalState(mergeDrillApproval(out, simId));
    toast("Live-approval granted · Live — using real approval", "go");
  };

  const runLive = async () => {
    if (!seat.liveRunAllowed) {
      toast("Live run sealed", "warn");
      return;
    }
    if (!simId) { toast("Run a practice drill first", "warn"); return; }
    const ih = String((approvalState && approvalState.intent_hash) || `drill:${simId}`).trim();
    let needsYouId = String((approvalState && approvalState.needs_you_id) || "").trim();
    if (!needsYouId) {
      try {
        const listed = await api(safeDrillPath("get__ai_needs-you"), { method: "GET" });
        const items = (listed && listed.items) || [];
        const hit = items.find((it) => {
          const payload = (it && it.payload && typeof it.payload === "object") ? it.payload : {};
          return String(it.canonical_incident_id || "") === simId
            || String(payload.intent_hash || "") === ih;
        });
        needsYouId = String((hit && hit.needs_you_id) || "").trim();
      } catch (_) { /* keep empty */ }
    }
    if (!needsYouId) {
      toast("Request live approval first — Needs You item missing", "warn");
      return;
    }
    const minted = await run(safeDrillPath("post__ai_needs-you_approval-token"), {
      method: "POST",
      body: {
        needs_you_id: needsYouId,
        intent_hash: ih,
        canonical_incident_id: simId,
      },
    });
    if (!minted || !minted.ok || !minted.approval_token) {
      toast("Allow-once token refused — live drill stays sealed", "bad");
      return;
    }
    const out = await run(safeDrillPath("post__simulations_p_run", { pathParams: [simId] }), {
      body: {
        mode: "live",
        approval_token: minted.approval_token,
        needs_you_id: needsYouId,
        intent_hash: ih,
        world_state_version: Number(minted.world_state_version || 0),
        policy_revision: minted.policy_revision,
      },
    });
    if (!out) { toast("Live run refused — approval may be required", "bad"); return; }
    toast("Live simulation run accepted", "warn");
    if (seat.reportAllowed) {
      try {
        const report = await api(safeDrillPath("get__simulations_p_report", { pathParams: [simId] }), { method: "GET" });
        setSteps(drillStepsClampOf(drillStepsOf(report, out), seat.stepsPaintMax));
      } catch (_) { /* keep prior steps */ }
    }
  };

  const paintSteps = steps ? drillStepsClampOf(steps, seat.stepsPaintMax) : null;
  const approvalJson = approvalState
    ? JSON.stringify(approvalState, null, 2).slice(0, seat.approvalJsonMax)
    : "";

  const alertTone = engineOff || seat.detail === "drill_admin_only" ? "warn" : "info";
  const alertTitle = engineOff
    ? "Simulation is off on this deployment"
    : seat.detail === "drill_admin_only"
      ? "Drill is sealed for this account"
      : "Live response drill";
  const alertSay = engineOff
    ? "Simulations are off on the server. Practice runs stay sealed."
    : seat.detail === "drill_admin_only"
      ? "Practice and live-approval need a manager seat."
      : "Create a practice run, run it safely, then request human approval for a live drill.";

  return html`<div className="drill-desk drill-desk--liquid drill-v2"
    data-cmc-page="page92-drill" data-cmc-drill="page39" data-cmc-residual="page188-drill"
    data-cmc-prepare-ux="drill-v2">
    <div className="drill-desk__rail">
      <div>
        <p className="eyebrow">Prepare · Drills</p>
        <h2 className="drill-desk__title">${engineOff ? "Practice engine is off" : seat.writeAllowed ? "Rehearse without consequences" : "Drill seat sealed"}</h2>
        <p className="note">Build the scenario, run it safely, inspect every step, then request a human-approved live rehearsal only when needed.</p>
      </div>
      <div className="btn-row drill-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${busy || posture.loading || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
      </div>
    </div>

    <${Alert} tone=${alertTone} title=${alertTitle} say=${alertSay}/>
    ${envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
      : null}

    <div className="drill-desk__kpis drill-v2__safety-line">
      <div className="drill-desk__kpi" data-on=${seat.engineArmed ? "1" : "0"}>
        <span>Engine</span>
        <b>${posture.loading ? "…" : (seat.engineArmed ? "on" : "off")}</b>
        <em>practice</em>
      </div>
      <div className="drill-desk__kpi" data-on=${seat.dryRunAllowed ? "1" : "0"}>
        <span>Practice</span>
        <b>${seat.dryRunAllowed ? "open" : "sealed"}</b>
        <em>practice only</em>
      </div>
      <div className="drill-desk__kpi" data-on=${seat.liveApproveAllowed ? "1" : "0"}>
        <span>Grant</span>
        <b>${seat.liveApproveAllowed ? "open" : "sealed"}</b>
        <em>live rehearsal</em>
      </div>
      <div className="drill-desk__kpi" data-on="0">
        <span>Seat</span>
        <b>${isSaStaff ? "staff" : isUser ? "user" : (role || "—")}</b>
        <em>site data only</em>
      </div>
    </div>

    ${posture.error ? html`<${Alert} tone="warn" title="Could not confirm access" say=${softResponseErrorOf(posture.error)}/>` : null}

    <div className="drill-v2__switch" role="tablist" aria-label="Drill workflow">
      ${[["practice", "1", "Practice safely", "No production actions"], ["live", "2", "Approve live", "Human decision required"]].map(([id, n, l, say]) => html`
        <button key=${id} type="button" role="tab" aria-selected=${pane===id?"true":"false"}
          data-on=${pane===id?"1":"0"} onClick=${()=>setPane(id)}>
          <i>${n}</i><span><b>${l}</b><small>${say}</small></span>
        </button>`)}
      <button type="button" className="drill-v2__back" onClick=${()=>go("/prepare/readiness")}>Review readiness</button>
    </div>

    ${pane === "practice" && html`
      <div className="drill-v2__practice-grid">
      <${Card} title="Design the rehearsal" meta="isolated practice">
        <${Field} label="Drill name">
          <${Input} icon="book" value=${name} onChange=${e=>setName(e.target.value)}/>
        <//>
        <${Field} label="Scenario">
          <select className="inp" value=${eventType} onChange=${e=>setEventType(e.target.value)}
            disabled=${engineOff || !seat.createAllowed}>
            ${SCENARIOS.map((s) => html`<option key=${s.id} value=${s.id}>${s.label}</option>`)}
          </select>
        <//>
        <button type="button" className="btn btn--go btn--sm"
          disabled=${busy || !seat.createAllowed || !seat.dryRunAllowed} onClick=${runPractice}
          style=${{ marginTop:10 }}>
          ${busy ? "Running…" : "Start safe rehearsal"}</button>
        ${error && html`<${Alert} tone="bad" title="Drill failed" say=${softResponseErrorOf(error)}/>`}
        ${simId && html`<${KV} k="Run" v=${simId}/>`}
        ${!seat.createAllowed
          ? html`<p className="note">Practice sealed until Live opens the seat.</p>`
          : null}
      <//>
      <aside className="drill-v2__guardrail">
        <span className="drill-v2__guardrail-pulse"/>
        <p className="eyebrow">Safety boundary</p>
        <h3>Production stays untouched</h3>
        <p>No live alarm, dispatch or notification is emitted during practice.</p>
        <small>${simId ? "Run " + simId + " is ready for review" : "Create a rehearsal to begin"}</small>
      </aside>
      </div>
      ${paintSteps && html`
        <${Card} title="What happened" meta="step-by-step replay" cls="mt drill-v2__report">
          <div className="rows drill-v2__timeline">
            ${!paintSteps.length
              ? html`<p className="note">No practice steps recorded yet.</p>`
              : paintSteps.map((s, i) => html`
                  <div key=${i} className="row row--say drill-v2__step" data-ok=${s.ok ? "1" : "0"}>
                    <i className="drill-v2__step-no">${i + 1}</i>
                    <${Ico} d="bolt" w=${14}/>
                    <span className="main">
                      <b>${s.step || s.name || ("step-" + i)}</b>
                      <span>${s.executor || s.handler || "—"} · ${s.ok ? "ok" : "fail"}
                        ${s.duration_ms != null ? " · " + s.duration_ms + " ms" : ""}</span>
                    </span>
                  </div>`)}
          </div>
        <//>`}
    `}

    ${pane === "live" && html`
      <${Card} title="Human approval chain" meta="live rehearsal">
        <${KV} k="Run" v=${simId || "— (run practice first)"}/>
        <${Field} label="Reason / note">
          <${Input} icon="alert" value=${approvalNote} onChange=${e=>setApprovalNote(e.target.value)}/>
        <//>
        <div className="btn-row drill-desk__acts" style=${{ marginTop:10 }}>
          <button type="button" className="btn btn--sm"
            disabled=${busy || !simId || !seat.liveRequestAllowed} onClick=${requestLive}>Request review</button>
          <button type="button" className="btn btn--sm btn--ghost"
            disabled=${busy || !simId || !seat.approvalReadAllowed} onClick=${refreshApproval}>Refresh</button>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !simId || !seat.liveApproveAllowed} onClick=${approveLive}>Grant approval</button>
          <button type="button" className="btn btn--danger btn--sm"
            disabled=${busy || !simId || !seat.liveRunAllowed} onClick=${runLive}>Start live rehearsal</button>
        </div>
        ${!seat.liveApproveAllowed
          ? html`<p className="note">Approval sealed until Live opens the seat.</p>`
          : null}
        ${approvalState && html`<${Sec} title="Approval state" meta="approval state">
          <pre className="note" style=${{ whiteSpace:"pre-wrap", fontFamily:"var(--mono)", fontSize:"11px", margin:0 }}>
${approvalJson}</pre>
        <//>`}
        <p className="note">Live drill approval only after a successful approve write.</p>
      <//>`}
  </div>`;
}
